import React from 'react';

function About() {
  return (
    <div className="prose max-w-none">
      <h1>Building poisdata.org: AI and Data Science for POIS Research</h1>

      <p>This project is dedicated to the systematic collection, processing, and analysis of information from diverse sources, aiming to construct a structured knowledge base accessible to both researchers and patients. The database is envisioned as a valuable resource for in-depth POIS research and a platform for collaborative knowledge building.</p>

      <h3>Accelerated Development with AI Tools</h3>
      <p>The development of poisdata.org has been significantly accelerated by leveraging multiple AI-powered tools:</p>
      <ol>
        <li>
          <strong><a href="https://www.cursor.com/">Cursor</a></strong>: An intelligent code editor that provides code generation, autocompletion, and debugging capabilities. Cursor has streamlined development by expediting tasks like writing boilerplate code, refactoring, and identifying bugs.
        </li>
        <li>
          <strong><a href="https://deepseek.ai/">DeepSeek V3</a></strong>: A powerful large language model accessed through <a href="https://nebius.com/services/studio-inference-service">Nebius AI Studio</a>. DeepSeek V3 excels at code generation and technical tasks, offering cost-effective inference compared to proprietary models.
        </li>
        <li>
          <strong>AI-Powered Processing Pipeline</strong>: The project utilizes AI for several key processing tasks:
          <ul>
            <li>Named Entity Recognition for medical terms</li>
            <li>Case study generation from forum discussions</li>
            <li>Topic extraction and summarization</li>
            <li>Automated data quality checks</li>
          </ul>
        </li>
      </ol>

      <p>The adoption of these AI tools has dramatically improved the development process. Development velocity has increased significantly, while code quality has improved through AI-assisted review. The AI-powered processing workflows have proven more efficient than traditional approaches, and the automation of repetitive coding tasks has freed up time for focusing on core research and development objectives.</p>

      <h3>Data Acquisition and Preprocessing</h3>
      <p>Data acquisition is performed from two primary sources: the <a href="https://poiscenter.com">POISCenter forum</a> and the <a href="https://www.reddit.com/r/pois/">Reddit /r/POIS community</a>.</p>

      <h4>POISCenter Forum Data</h4>
      <p>For the <a href="https://poiscenter.com">POISCenter forum</a>, <a href="https://apify.com">Apify's web scraping platform</a> is employed. Over 40,000 discussion posts spanning from March 2011 to January 2025 have been collected. Collected raw HTML pages undergo a preprocessing pipeline. This pipeline includes HTML-to-markdown conversion, author attribution cleaning, URL normalization, and duplicate detection. The preprocessing is executed using Python scripts, ensuring data cleanliness and consistency.</p>

      <div className="my-6">
        <img
          src="/images/poiscenter-screenshot.png"
          alt="Screenshot of POISCenter forum showing various discussion categories and topics"
          className="w-full rounded-lg shadow-lg border border-gray-200"
        />
        <p className="text-sm text-gray-600 mt-2 text-center">POISCenter forum interface showing discussion categories and recent topics</p>
      </div>

      <h4>Reddit /r/POIS Data</h4>
      <p>Data from <a href="https://www.reddit.com/r/pois/">Reddit's /r/POIS subreddit</a> is gathered using the official Reddit API with OAuth2 authentication. Features implemented for Reddit data collection include paginated post retrieval, comment tree expansion to capture comprehensive discussions, rate limit handling to ensure API compliance, and deduplication against existing data to maintain data integrity. Python scripts are utilized for this collection process, emphasizing efficiency and adherence to platform guidelines.</p>

      <div className="my-6">
        <img
          src="/images/reddit-pois-screenshot.png"
          alt="Screenshot of Reddit r/POIS subreddit showing recent posts and community information"
          className="w-full rounded-lg shadow-lg border border-gray-200"
        />
        <p className="text-sm text-gray-600 mt-2 text-center">Reddit r/POIS subreddit interface displaying recent discussions and community details</p>
      </div>

      <h3>System Architecture and Key Components</h3>
      <p>A modern technology stack is leveraged for the system's architecture. Key components include:</p>

      <ul>
        <li>
          <strong>Temporal Workflow Engine</strong>: Complex data processing pipelines are orchestrated by <a href="https://temporal.io/">Temporal.io</a>. These pipelines encompass AI-powered case study generation, named entity recognition and categorization, automated data quality checks, and topic extraction and page generation.
        </li>

        <div className="my-6">
          <img
            src="/images/temporalio-admin-ui.png"
            alt="Screenshot of Temporal.io admin UI showing workflow executions and monitoring"
            className="w-full rounded-lg shadow-lg border border-gray-200"
          />
          <p className="text-sm text-gray-600 mt-2 text-center">Temporal.io admin interface displaying workflow executions and monitoring dashboard</p>
        </div>

        <li>
          <strong>PostgREST API</strong>: A RESTful interface to the <a href="https://www.postgresql.org/">PostgreSQL</a> database is provided by <a href="https://postgrest.org/">PostgREST</a>. Auto-generated endpoints for all database tables and views, JWT authentication, and row-level security are incorporated, ensuring secure and streamlined data access.
        </li>
        <li>
          <strong>React Frontend</strong>: A modern web interface is built with <a href="https://reactjs.org/">React</a>. Features include a statistics dashboard, case study exploration tools, entity relationship visualizations, and an administrative interface for workflow management.
        </li>
      </ul>

      <h3>Database Models</h3>
      <p><a href="https://sqlmodel.tiangolo.com/">SQLModel</a> is employed for database schema management. Key database models include `Message`, `AuthorDiscussion`, `CaseStudy`, and `Entity`. Relationships between these models are defined to represent the data structure, including messages being processed into author discussions, author discussions aggregated into case studies, and case studies containing entities.</p>

      <h3>Data Processing Pipeline</h3>
      <h4>Message Aggregation and Author Discussions</h4>
      <p>The system processes raw messages from both <a href="https://poiscenter.com">POISCenter</a> and <a href="https://www.reddit.com/r/pois/">Reddit</a> into meaningful "Author Discussions". This aggregation step is crucial for building comprehensive case studies. Here's how it works:</p>

      <ol>
        <li>
          <strong>Message Collection</strong>: Individual posts and comments are first collected into the Messages table, preserving their original structure and metadata:
          <ul>
            <li>Author attribution</li>
            <li>Parent-child relationships (for comments)</li>
            <li>Source platform</li>
            <li>Publication timestamp</li>
            <li>Original URLs</li>
          </ul>
        </li>
        <li>
          <strong>Thread Reconstruction</strong>: Messages are then grouped into coherent discussion threads:
          <ul>
            <li>Posts and their associated comments are linked</li>
            <li>Cross-referenced posts by the same author are connected</li>
            <li>Temporal relationships between discussions are preserved</li>
          </ul>
        </li>
      </ol>

      <h3>Relevance Scoring System</h3>
      <p>Each Author Discussion undergoes AI-powered relevance scoring to determine its value for case study generation. The scoring system evaluates multiple factors:</p>

      <h4>Scoring Criteria</h4>
      <ul>
        <li>Detailed symptom descriptions</li>
        <li>Personal experiences and timeline</li>
        <li>Treatment attempts and outcomes</li>
        <li>Triggers and patterns</li>
        <li>Impact on daily life</li>
        <li>Scientific observations</li>
        <li>Information clarity and coherence</li>
        <li>Cross-post consistency</li>
      </ul>

      <h4>Example Relevance Scoring Prompt</h4>
      <pre className="text-sm bg-gray-50 p-4 rounded-lg overflow-x-auto text-gray-800">
        {`You are an expert at analyzing POIS related discussions and evaluating their relevance for case study generation.

Consider the following factors when scoring:
1. Detailed symptom descriptions
2. Personal experiences and timeline of condition
3. Treatment attempts and their outcomes
4. Triggers and patterns observed
5. Impact on daily life and quality of life
6. Clarity and coherence of information
7. Consistency across multiple posts/comments
8. Scientific or medical observations

For each discussion, provide a relevance score between 0.0 and 1.0 where:
- 0.0: No relevant information for case study
- 0.3: Basic mentions of POIS but lacks detail
- 0.5: Some useful information but incomplete
- 0.7: Good quality information with details
- 1.0: Excellent, comprehensive information`}
      </pre>

      <h4>Example Author Discussion Summary Prompt</h4>
      <pre className="text-sm bg-gray-50 p-4 rounded-lg overflow-x-auto text-gray-800">
        {`Extract ONLY concrete facts from the author's content, considering the provided relevance score and reasoning. Categories include:

- Symptoms/Experiences: Specific physical/mental symptoms
- Treatments/Outcomes: Substances used, duration, effects
- Medical Data: Test results, diagnoses, clinical findings
- Timeline: Event sequences with temporal context
- Observations: Other POIS-relevant factual data

FORMATTING RULES:
- Use EXACT headers: **Symptoms**, **Treatments**, etc.
- Bullet points only - no prose
- Omit ENTIRE category if no relevant data
- If ALL categories empty → "No POIS-related facts found"

PRIORITIZATION:
- Focus on facts highlighted in the relevance reasoning
- Give more weight to highly-scored content (relevance score > 0.7)
- For lower scores (0.5-0.7), be more selective about included facts`}
      </pre>

      <h4>Example Case Study Generation Prompt</h4>
      <pre className="text-sm bg-gray-50 p-4 rounded-lg overflow-x-auto text-gray-800">
        {`You are an expert medical data aggregator specializing in POIS case study creation.
You have aggregated data for each author's POIS-related discussions, including:
1. A relevance reasoning (justification of why the data is pertinent to POIS)
2. A discussion summary (the consolidated facts from the author's POIS-related discussions)
3. A relevance score (numerical significance of the discussion)

OBJECTIVES:
• Combine the discussion summary and relevance reasoning into a cohesive case study narrative
• Use clear, structured formatting suitable for clinical or research review
• Maintain accuracy, relying strictly on the provided data—avoid guesswork
• Include where possible: background, timeline, treatments/outcomes, medical data
• Conclude with key insights and open-ended questions from the data
• Keep tone professional and medically oriented while remaining accessible
• Apply minimal markdown formatting for readability

REMEMBER:
• Only incorporate details that appear in the content
• Do not invent, speculate, or expand upon the facts`}
      </pre>

      <p>These three prompts work together in sequence to process raw discussion data into structured case studies:</p>
      <ol>
        <li>The relevance scoring prompt evaluates the quality and completeness of author discussions</li>
        <li>The discussion summary prompt extracts concrete facts from highly-scored content</li>
        <li>The case study generation prompt combines these facts into a cohesive medical narrative</li>
      </ol>

      <h3>Web Interface Features</h3>
      <p>The React-based web interface provides several key features for exploring and understanding POIS data:</p>

      <h4>Entity Type Navigation</h4>
      <ul>
        <li>A sidebar allows users to browse entities categorized by type (e.g., Symptoms, Medications, Supplements)</li>
        <li>Icons visually represent each entity type for easy navigation</li>
      </ul>

      <h4>Entity List and Exploration</h4>
      <ul>
        <li>For each entity type, users can view a list of entities, grouped by category and sub-category</li>
        <li>Interactive bar charts visualize the top entity groups and sub-groups by mention count</li>
        <li>Search functionality enables users to quickly find specific entities within a type</li>
        <li>Users can expand groups and sub-groups to see more entities</li>
        <li>Links to individual entity detail pages for in-depth exploration</li>
      </ul>

      <h4>Case Study Pages</h4>
      <ul>
        <li>Displays the full text of a case study in a readable format</li>
        <li>Includes a list of "Author Discussions" related to the case study author, retrieved from forums and Reddit</li>
        <li>Author discussions are summarized and scored for relevance, aiding in further exploration</li>
        <li>Pagination for browsing author discussions</li>
      </ul>

      <h4>Statistics Dashboard</h4>
      <ul>
        <li>Accessible from the homepage, providing an overview of the database</li>
        <li>Key statistics include:
          <ul>
            <li>Total messages and discussions processed</li>
            <li>Number of unique authors and case studies</li>
            <li>Distribution of data sources (Reddit vs. PoisCenter)</li>
            <li>Histogram of author relevance scores</li>
            <li>Top authors ranked by relevance score, with links to their case studies</li>
          </ul>
        </li>
        <li>Interactive pie charts and bar charts visualize data distributions</li>
        <li>Paginated display of top authors</li>
      </ul>
    </div>
  );
}

export default About;